import { useCallback, useEffect, useState } from "react";
import { useDataAdapter } from "../data/DataAdapterContext";
import type { NeedsAttentionItem } from "../data/needs-attention-types";

/** How many a page's strip shows: an order with more is a case for the Needs attention page. */
const STRIP_LIMIT = 20;

/**
 * The open and acknowledged alerts on one order (with its lines and purchase
 * orders) or one purchase order, for its page's strip (v1 completion I §10).
 * A caller who may not read Needs attention, or a failed read, sees none: the
 * strip is a pointer, never the only place an alert is shown.
 */
export function useSubjectAlerts(subject: {
  orderNo?: string;
  purchaseOrderNo?: string;
}): {
  alerts: NeedsAttentionItem[];
  reload: () => void;
} {
  const adapter = useDataAdapter();
  const [alerts, setAlerts] = useState<NeedsAttentionItem[]>([]);
  const [reloads, setReloads] = useState(0);
  const { orderNo, purchaseOrderNo } = subject;

  useEffect(() => {
    if (!orderNo && !purchaseOrderNo) return;
    let cancelled = false;
    adapter
      .listNeedsAttention({
        orderNo,
        purchaseOrderNo,
        sort: "due_at,priority",
        pageSize: STRIP_LIMIT,
      })
      .then((list) => {
        if (!cancelled) setAlerts(list.items);
      })
      .catch(() => {
        if (!cancelled) setAlerts([]);
      });
    return () => {
      cancelled = true;
    };
  }, [adapter, orderNo, purchaseOrderNo, reloads]);

  return { alerts, reload: useCallback(() => setReloads((n) => n + 1), []) };
}
